import { useRef } from "react";
import { debounce } from "lodash";
import { useQuery } from "@tanstack/react-query";
import ReactSelect from "react-select";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SemvInput } from "../../../../components/ui/input";
import { semvApi } from "../../../../shared/services/axios/apis.service";
import { DehydratedGovPlanProjectContract } from "./types/project";
import { DehydratedGovPlanSecretaryProject } from "./model/project";
import { Table } from "../../../../components/page-releated/grp-standard-screen/table";
import { projectColumns } from "./columns";
import {
    CreateSecretaryProject,
    CreateSecretaryProjectModalHandles,
} from "./create";
import { GOVERNMENT_PLAN_STATUS } from "./types/status";

const statusOptions = [
    { value: "all", label: "Todos" },
    {
        value: GOVERNMENT_PLAN_STATUS.COMPLETED,
        label: "Concluidos",
    },
    {
        value: GOVERNMENT_PLAN_STATUS.COMPLETED_LATE,
        label: "Concluidos com atraso",
    },
    {
        value: GOVERNMENT_PLAN_STATUS.IN_PROGRESS,
        label: "Em andamento",
    },
];

export const SecretaryProjects = () => {
    const navigation = useNavigate();
    const creationRef = useRef<CreateSecretaryProjectModalHandles>(null);
    const [secretaryName, id] = useParams().secretaryId?.split("|@|") || [];
    const [searchParams, setSearchParams] = useSearchParams();

    const debounceRef = useRef(
        debounce((searchParams$: URLSearchParams) => {
            setSearchParams(searchParams$);
        }, 300)
    );

    const { data } = useQuery({
        queryKey: ["gov-plan-secretary-projects", id, searchParams.toString()],
        queryFn: async () => {
            const response = await semvApi.get<{
                projects: DehydratedGovPlanProjectContract[];
            }>(
                `/government-plan/projects?secretary=${id}&${searchParams.toString()}`
            );

            return response.data.projects.map(
                (it) => new DehydratedGovPlanSecretaryProject(it)
            );
        },
        initialData: [],
    });

    return (
        <>
            <header className="w-full h-28 border-b border-slate-500 flex justify-between">
                <h5 className="text-slate-700 pt-5 pl-4">
                    Projetos {secretaryName}
                </h5>

                <section className="self-end flex space-x-4">
                    <button
                        type="button"
                        className="btn-primary mb-2"
                        onClick={() => creationRef.current?.open(+id)}
                    >
                        Cadastrar
                    </button>
                </section>
            </header>
            <div className="grid grid-cols-4 gap-1 items-center mt-2">
                <SemvInput
                    name="searchTerm"
                    label="Nome / Código"
                    defaultValue={searchParams.get("searchTerm") || ""}
                    onChange={(e) => {
                        const draft = new URLSearchParams(
                            searchParams.toString()
                        );
                        draft.set("searchTerm", e.target.value);
                        debounceRef.current(draft);
                    }}
                />
                <div>
                    <ReactSelect
                        id="status-filter"
                        defaultValue={
                            statusOptions.find(
                                (it) => it.value === searchParams.get("status")
                            ) || { value: "all", label: "Todos" }
                        }
                        isMulti={false}
                        onChange={(option) => {
                            const draft = new URLSearchParams(searchParams);
                            if (option?.value === "all") draft.delete("status");
                            else draft.set("status", option?.value as string);
                            setSearchParams(draft);
                        }}
                        options={statusOptions}
                    />
                </div>
            </div>
            <Table
                dataSource={data}
                columns={projectColumns()}
                onRowClick={(record) => navigation(`projetos/${record.id}`)}
            />
            <CreateSecretaryProject ref={creationRef} />
        </>
    );
};
